import type { BreakPatch, LiveBreakHostDetail } from "@hobbyshopify/backend-client";
import { SubtreeController } from "subtree.js/core";
import type { AppDeps } from "../../app-deps";
import { cancelBreak, loadBreakHostDetail, promoteBreak, updateBreak, uploadLiveCover } from "../../core/ops/live-ops";
import { thrownText } from "../../core/primitives/errors";
import { readImageFile } from "../../core/primitives/image-upload";
import { toDateTimeLocal } from "../../core/primitives/lineup";
import { refundsFailedText } from "../../core/primitives/live";
import {
  busyForm,
  failedForm,
  failedLoad,
  idleForm,
  loaded,
  loading,
  savedForm,
} from "../../core/primitives/load-status";
import { LiveBreakActions, LiveBreakState } from "./live-break-model";

export type LiveBreakDeps = Pick<AppDeps, "client" | "authHandler" | "shareLinks">;

export interface LiveBreakRouting {
  /** Back to the queue, after the break is cancelled. */
  toQueue(): void;
}

/** One break on sale: what buyers see on its tile, its spots and its money. */
export class LiveBreakController extends SubtreeController implements LiveBreakActions {
  readonly state = new LiveBreakState();

  constructor(
    readonly deps: LiveBreakDeps,
    readonly routing: LiveBreakRouting,
    readonly breakId: string,
  ) {
    super();
    this.subtree.put(LiveBreakState, this.state);
    this.subtree.put(LiveBreakActions, this);
    this.sync(() => {
      const b = this.state.breakRow.value;
      this.state.share.value = b && b.status !== "DRAFT" && b.status !== "CANCELLED" ? deps.shareLinks.break(b) : null;
    }, [this.state.breakRow]);
    void this.reload();
  }

  reload = async () => {
    this.state.status.value = loading;
    const r = await loadBreakHostDetail({ ...this.deps, breakId: this.breakId });
    if (this.isDisposed) return;
    if (!r.ok) {
      this.state.status.value = failedLoad(r.errors);
      return;
    }
    this.#stream(r.value);
    this.state.status.value = loaded;
  };

  setTitle = (title: string) => {
    this.state.title.value = title;
  };

  saveTitle = async () => {
    const title = this.state.title.value.trim();
    if (title === "" || title === this.state.breakRow.value?.title || this.state.saveTitle.value.busy) return;
    this.state.saveTitle.value = busyForm;
    const error = await this.#update({ title });
    if (this.isDisposed) return;
    this.state.saveTitle.value = error ? failedForm(error) : savedForm("Saved");
  };

  setBreakTime = (breakTime: string) => {
    this.state.breakTime.value = breakTime;
  };

  saveBreakTime = async () => {
    if (this.state.saveBreakTime.value.busy) return;
    const typed = this.state.breakTime.value;
    this.state.saveBreakTime.value = busyForm;
    const error = await this.#update({ breakNotBefore: typed ? new Date(typed) : null });
    if (this.isDisposed) return;
    this.state.saveBreakTime.value = error ? failedForm(error) : savedForm("Saved");
  };

  uploadCover = async (file: File) => {
    if (this.state.cover.value.busy) return;
    this.state.cover.value = busyForm;
    try {
      // `readImageFile` throws on an oversized or unreadable file; the upload's own refusal is the Result.
      const r = await uploadLiveCover({ ...this.deps, image: await readImageFile(file) });
      if (this.isDisposed) return;
      if (!r.ok) {
        this.state.cover.value = failedForm(r.errors);
        return;
      }
      const error = await this.#update({ imageUrl: r.value.url });
      if (this.isDisposed) return;
      this.state.cover.value = error ? failedForm(error) : idleForm;
    } catch (error) {
      if (this.isDisposed) return;
      this.state.cover.value = failedForm(thrownText(error, "Upload failed."));
    }
  };

  cancelBreak = async () => {
    if (this.state.cancel.value.busy) return;
    if (!this.state.confirmCancel.value) {
      this.state.confirmCancel.value = true;
      return;
    }
    this.state.cancel.value = busyForm;
    const r = await cancelBreak({ ...this.deps, breakId: this.breakId });
    if (this.isDisposed) return;
    if (!r.ok) {
      this.state.cancel.value = failedForm(r.errors);
      return;
    }
    this.state.confirmCancel.value = false;
    // A refused refund keeps the host here, on the cancelled break, until it is read.
    if (r.value.refundsFailed > 0) {
      this.state.cancel.value = failedForm(refundsFailedText(r.value.refundsFailed));
      await this.reload();
      return;
    }
    this.state.cancel.value = idleForm;
    this.routing.toQueue();
  };

  keepBreak = () => {
    this.state.confirmCancel.value = false;
    this.state.cancel.value = idleForm;
  };

  promote = async () => {
    if (this.state.promote.value.busy) return;
    this.state.promote.value = busyForm;
    const r = await promoteBreak({ ...this.deps, breakId: this.breakId });
    if (this.isDisposed) return;
    if (!r.ok) {
      this.state.promote.value = failedForm(r.errors);
      return;
    }
    const b = this.state.breakRow.value;
    if (b) this.state.breakRow.value = { ...b, promotedAt: r.value.promotedAt };
    this.state.promote.value = savedForm(`Sent to ${r.value.sent} phone${r.value.sent === 1 ? "" : "s"}`);
  };

  /** The saved break replaces the shown one; the refusal comes back as the errors to stream. */
  async #update(patch: BreakPatch): Promise<readonly Error[] | null> {
    const r = await updateBreak({ ...this.deps, breakId: this.breakId, patch });
    if (this.isDisposed) return null;
    if (!r.ok) return r.errors;
    this.#stream(r.value);
    return null;
  }

  #stream(detail: LiveBreakHostDetail): void {
    this.state.breakRow.value = detail.break;
    this.state.slots.value = detail.slots;
    this.state.title.value = detail.break.title;
    this.state.breakTime.value = toDateTimeLocal(detail.break.breakNotBefore);
  }
}
